import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  EditorContext,
  UpdateType,
} from "../components/Dashboard/Editor/EditorContextProvider";
import { EditType } from "../components/Dashboard/Editor/EditorSwiper/EditorSwiper";
import {
  deleteLayerUpdate,
  updateLayerUpdate,
} from "../redux/action/CycleManager-action";
import {
  setReports,
  updateReportsLayerMap,
} from "../redux/action/Dashboard-action";
import {
  asOfDateFieldName,
  currAsOfDateFieldName,
  currValidationFieldName,
  graphicsLayer,
  isdeletedFieldName,
  view,
} from "../utils/API";
import { applyEditsInBatches, isReportsLayer } from "../utils/helper";
import {
  handleAttachments,
  handleGeometryUpdates,
  processFieldsAndAttributes,
  queryUpdatedFeatures,
  updateFeatureAttributes,
  updateFeaturesList,
} from "./Editor/helpers";

const numericFields = [
  "small-integer", // Short - 16-bit integers (-32,768 to 32,767)
  "integer", // Long - 32-bit integers (-2.147B to 2.147B)
  "big-integer", // 64-bit integers (-9.223Q to 9.223Q)
  "single", // Float - 32-bit floating point
  "double", // Double - 64-bit floating point
  "float",
];

function mergeFeatures(original, updated) {
  // Create a map of existing reports by objectId
  const reportMap = new Map(original.map((f) => [f.getObjectId(), f]));

  // Replace or add features from updated
  updated.forEach((f) => {
    reportMap.set(f.getObjectId(), f);
  });

  // Return as array
  return Array.from(reportMap.values());
}

export const trimStrings = (obj) => {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }
  const trimmedObj = {};
  for (const key in obj) {
    if (typeof obj[key] === "string") {
      trimmedObj[key] = obj[key].trim();
    } else {
      trimmedObj[key] = obj[key];
    }
  }
  return trimmedObj;
};

const convertNumber = (value, f) => {
  let newValue = value;
  try {
    if (f.type === "big-integer") {
      newValue = BigInt(value);
    } else {
      newValue = Number(value);
    }
  } catch (err) {
    console.log(err);
  }

  return newValue;
};

const useEditor = ({ editType, config, onSave, goBack, openSnackbar }) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation("common");
  const {
    highlightFeature,
    attachments,
    addedPoints,
    batchUpdateFeatures,
    editableLayer,
    setEditableFeatureList,
    editableFeatureList,
    updateType,
    setAttachments,
    setAddedPoints,
    trimmedSituationalFeatures,
    isReportsUpdate,
  } = useContext(EditorContext);
  const reportsLayer = useSelector((state) => state.dashboard.reportsLayer);
  const reports = useSelector((state) => state.dashboard.reports);
  const reportsLayerMap = useSelector(
    (state) => state.dashboard.reportsLayerMap
  );

  const methods = useForm();

  const dispatch = useDispatch();
  const touchedFields = methods?.formState?.touchedFields || {};
  const touchedFieldNames = Object.keys(touchedFields);

  const toggleLoading = (state) => {};
  /**
   * Returns the geometry that will be added as a new feature to the map
   * @returns {*}
   */
  const getGeometry = () => {
    if (!addedPoints || addedPoints.length === 0) return;

    if (editableLayer.geometryType === "point") {
      return addedPoints[0];
    } else if (editableLayer.geometryType === "polyline") {
      return addedPoints[0];
    } else if (editableLayer.geometryType === "polygon") {
      return addedPoints[0];
    }
  };

  const confirmStatus = async () => {
    try {
      let features = [];

      if (highlightFeature) {
        features.push(highlightFeature);
      } else {
        batchUpdateFeatures.forEach((item) => {
          features.push(item.feature);
        });
      }

      if (features.length === 0) {
        console.warn("No features to update");
        return;
      }

      const today = new Date();

      features = features.map((feat) => {
        let fieldName = currAsOfDateFieldName;
        if (updateType === UpdateType.baseline) {
          fieldName = asOfDateFieldName;
        }

        feat.attributes[fieldName] = today.getTime();
        return feat.clone();
      });

      setLoading(true);

      await applyEditsInBatches(editableLayer, features);
      // await editableLayer.applyEdits({
      //   updateFeatures: features,
      // });
      editableLayer.refresh();
      // setBatchUpdateFeatures([]);
      // for quick lookup
      const featureMap = new Map(
        features.map((feat) => [feat.getObjectId(), feat])
      );

      dispatch(
        updateLayerUpdate({
          features: features,
          id: editableLayer.layerConfig.id,
        })
      );

      // Replace features in editableFeatureList
      const updatedEditableFeatureList = editableFeatureList.map(
        (editableFeature) =>
          featureMap.has(editableFeature.getObjectId())
            ? featureMap.get(editableFeature.getObjectId())
            : editableFeature
      );

      // Update the state
      setEditableFeatureList(updatedEditableFeatureList);
      // openSnackbar(t("screen.message.batchEditorReady"), 15000);
      onEditComplete({
        actionType: "confirm",
      });
    } catch (err) {
      console.log(err);

      openSnackbar(t("screen.message.error"), 15000);
      setLoading(false);
    }
  };

  const doEdit = async (data, event) => {
    const newBatchUpdateFeatures = batchUpdateFeatures.map(
      (item) => item.feature
    );

    const { atts, fieldTypes } = processFieldsAndAttributes({
      data,
      editableLayer,
      editType,
      config,
    });

    // Update attributes
    const updatedAtts = updateFeatureAttributes(atts, fieldTypes, {
      highlightFeature,
      newBatchUpdateFeatures,
      editableLayer,
      editType,
      touchedFieldNames,
    });

    if (
      editableLayer.layerConfig.isShownInReportManager &&
      editType === EditType.create
    ) {
      updatedAtts["reporttype"] = 1;
    }

    // Handle geometry
    const geometry = getGeometry();
    if (editType === EditType.create && !geometry) return;

    if (
      editType === EditType.edit &&
      editableLayer.geometryType !== "point" &&
      highlightFeature
    ) {
      // for roads
      highlightFeature.geometry = geometry;
    }

    setLoading(true);

    let updateFeatures = [];

    if (editType === EditType.edit) {
      updateFeatures = highlightFeature
        ? [highlightFeature]
        : newBatchUpdateFeatures;
    } else if (editableLayer.layerConfig.titleLabel === "roadsSituational") {
      updateFeatures = trimmedSituationalFeatures.updateFeatures;
    }

    const addedFeatures = [];
    if (editType === EditType.create) {
      const iso3 = new Set();
      if (graphicsLayer) {
        graphicsLayer.graphics.forEach((graphic) => {
          if (graphic?.id === "route-combined" && graphic?.attributes?.iso3) {
            iso3.add(graphic.attributes.iso3);
          }
        });
      }

      if (
        editableLayer.layerConfig.titleLabel === "roadsSituational" &&
        iso3.size > 1
      ) {
        const addedGraphics = graphicsLayer.graphics.toArray();
        addedGraphics.forEach((graphic) => {
          graphic.attributes = {
            ...updatedAtts,
            ...graphic.attributes,
          };
          addedFeatures.push({
            geometry: graphic.geometry,
            attributes: graphic.attributes,
          });
        });
      } else {
        addedFeatures.push({
          geometry: geometry,
          attributes: updatedAtts,
        });
      }
    }

    const {
      updateFeatures: processedFeatures,
      addedFeatures: processedAddedFeatures,
    } = await handleGeometryUpdates(updateFeatures, addedFeatures, editType);

    const featureIdsToRemove = new Set();
    if (trimmedSituationalFeatures.deleteFeatures.length > 0) {
      trimmedSituationalFeatures.deleteFeatures.forEach((feat) => {
        const doDelete = feat.attributes[isdeletedFieldName] !== 1;
        feat.attributes[isdeletedFieldName] = doDelete ? 1 : 2;
        featureIdsToRemove.add(feat.getObjectId());
      });
      processedFeatures.push(...trimmedSituationalFeatures.deleteFeatures);
    }

    try {
      const res = await editableLayer.applyEdits({
        addFeatures: editType === EditType.create ? processedAddedFeatures : [],
      });

      if (Array.isArray(res.addFeatureResults)) {
        res.addFeatureResults.forEach((result) => {
          if (result?.error) {
            throw new Error(`Error adding features: ${result.error.message}`);
          }
        });
      }

      if (processedFeatures.length > 0) {
        await applyEditsInBatches(editableLayer, processedFeatures);
      }

      if (EditType.create === editType && res.addFeatureResults.length > 0) {
        res.addFeatureResults.forEach((result, index) => {
          processedAddedFeatures[index].attributes[
            editableLayer.objectIdField
          ] = result.objectId;
        });
      }

      try {
        await handleAttachments({
          highlightFeature,
          addedFeatures: processedAddedFeatures,
          setAttachments,
          editType,
          attachments,
          editableLayer,
        });
      } catch (err) {
        console.log(err);
      }

      const updatedFeatures = await queryUpdatedFeatures({
        editableLayer,
        featuresToUpdate: [...processedAddedFeatures, ...processedFeatures],
      });

      editableLayer.refresh();

      if (updatedFeatures.length > 0) {
        if (isReportsLayer(editableLayer)) {
          const newReports = mergeFeatures([...reports], updatedFeatures);

          if (isReportsUpdate) {
            const feature = updatedFeatures[0];
            const id = feature.attributes.reportedlayerid;
            if (id) {
              const layerReports = reportsLayerMap[id] || [];

              const newLayerReports = mergeFeatures(
                layerReports,
                updatedFeatures
              );
              dispatch(
                updateReportsLayerMap({
                  id,
                  reportsArray: newLayerReports.map((r) => r.clone()),
                })
              );
            }
          }

          dispatch(setReports(newReports));
        }

        const newFeatures = updateFeaturesList({
          editableFeatureList,
          featureIdsToRemove,
          updatedFeatures,
        });

        dispatch(
          updateLayerUpdate({
            features: newFeatures,
            id: editableLayer.layerConfig.id,
          })
        );

        setEditableFeatureList(newFeatures);
      }

      onEditComplete({
        updateFeatures:
          EditType.edit === editType ? updateFeatures : processedAddedFeatures,
        actionType: "edit",
      });
    } catch (err) {
      console.log(err);
      openSnackbar(t("screen.message.error"), 15000);
      setLoading(false);
    }
  };

  const onEditComplete = ({ updateFeatures, actionType }) => {
    const handleFinish = () => {
      setLoading(false);
      view.graphics.removeAll();
      if (onSave) {
        onSave({ updateFeatures, actionType });
      } else {
        setAddedPoints([]);
        methods.reset();
      }
    };

    view
      .whenLayerView(editableLayer)
      .then((layerView) => {
        reactiveUtils
          .whenOnce(() => !layerView.updating)
          .then(() => {
            handleFinish();
          });
      })
      .catch((err) => {
        console.log(err);
        handleFinish();
      });
  };

  const getIsDeletedField = () => {
    if (!editableLayer) return;

    return editableLayer.fields.filter((f) => f.name === isdeletedFieldName)[0];
  };

  const updateReports = (features) => {
    const reportsIds = new Set(features.map((f) => f.getObjectId()));
    const filteredReports = reports.filter(
      (report) => !reportsIds.has(report.getObjectId())
    );

    const reportedLayerIds = new Set(
      features.map((f) => f.attributes.reportedlayerid)
    );

    if (reportedLayerIds.size > 0) {
      for (const layerId of reportedLayerIds) {
        const layerReports = reportsLayerMap[layerId];

        if (!layerReports) continue;
        const newLayerReports = layerReports.filter(
          (report) => !reportsIds.has(report.getObjectId())
        );
        dispatch(
          updateReportsLayerMap({
            id: layerId,
            reportsArray: newLayerReports,
          })
        );
      }
    }

    dispatch(setReports(filteredReports));
  };

  const doDelete = async (atts) => {
    try {
      if (!getIsDeletedField()) {
        if (goBack) {
          goBack();
        }
        return;
      }

      let features =
        batchUpdateFeatures.length > 0
          ? [...batchUpdateFeatures.map((item) => item.feature)]
          : [highlightFeature];
      setLoading(true);

      features.forEach((feat) => {
        const doDelete = feat.attributes[isdeletedFieldName] !== 1;
        feat.attributes[isdeletedFieldName] = doDelete ? 1 : 2;

        try {
          if (atts && Object.keys(atts || {}).length > 0) {
            Object.keys(atts || {}).forEach((key) => {
              feat.attributes[key] = atts[key];
            });
          }
        } catch (err) {
          console.log(err);
        }
      });

      await applyEditsInBatches(editableLayer, features);

      if (reportsLayer) {
        const objectIds = features.map((f) => f.getObjectId());
        const reportsOfTheFeatures = await reportsLayer.queryFeatures({
          outFields: [
            reportsLayer.objectIdField,
            currValidationFieldName,
            "reportedlayerid",
          ],
          where: `reportedfeatureid IN (${objectIds.join(",")})`,
        });

        if (
          Array.isArray(reportsOfTheFeatures?.features) &&
          reportsOfTheFeatures?.features?.length > 0
        ) {
          const updatedReports = reportsOfTheFeatures.features.map((f) => {
            f.attributes[currValidationFieldName] = 10; //Expired
            f.attributes[isdeletedFieldName] = 1;
            return f;
          });
          await applyEditsInBatches(reportsLayer, updatedReports);

          updateReports(updatedReports);
        }
      }

      editableLayer.refresh();
      view.graphics.removeAll();
      graphicsLayer.removeAll();

      dispatch(
        deleteLayerUpdate({
          id: editableLayer.layerConfig.id,
          features: features,
        })
      );

      // openSnackbar(t("screen.message.batchEditorReady"), 15000);
      onEditComplete({
        actionType: "delete",
      });

      // Remove all elements that are inside features from editableFeatureList
      const featureIdsToRemove = new Set(
        features.map((feature) => feature.getObjectId())
      );

      const updatedEditableFeatureList = editableFeatureList.filter(
        (editableFeature) =>
          !featureIdsToRemove.has(editableFeature.getObjectId())
      );
      setEditableFeatureList(updatedEditableFeatureList);

      //when deleting reports, we also need to remove them from the reports list in redux
      if (isReportsLayer(editableLayer)) {
        const featuresToRemove = new Set(features.map((f) => f.getObjectId()));

        const newReports = reports.filter(
          (r) => !featuresToRemove.has(r.getObjectId())
        );

        const id = editableLayer.layerConfig.viewer;
        const layerReports = reportsLayerMap[id];
        if (Array.isArray(layerReports)) {
          const newLayerReports = layerReports.filter(
            (r) => !featuresToRemove.has(r.getObjectId())
          );
          dispatch(
            updateReportsLayerMap({
              id,
              reportsArray: newLayerReports,
            })
          );
        }

        dispatch(setReports(newReports));
      }
    } catch (err) {
      console.log(err);

      openSnackbar(t("screen.message.error"), 15000);
      setLoading(false);
    }
  };

  return {
    doEdit,
    loading,
    doDelete,
    setLoading,
    methods,
    confirmStatus,
    getIsDeletedField,
  };
};

export default useEditor;
